<template>
    <section :class="['analytics-confidence-strip', { dark: isDark }]" aria-label="Analytics confidence">
        <div :class="['confidence-marker', activeGoal.status]">
            <i class="material-icons" aria-hidden="true">{{ statusIcon }}</i>
        </div>

        <div class="confidence-copy">
            <div class="confidence-meta">
                <p class="eyebrow">Analytics confidence</p>
                <span :class="['status-pill', activeGoal.status]">{{ formatStatus(activeGoal.status) }}</span>
            </div>
            <h2>{{ activeGoal.title }}</h2>
            <p>{{ confidenceSummary }}</p>
        </div>

        <router-link class="program-link" :to="programRoute">
            View analytics program
            <i class="material-icons" aria-hidden="true">arrow_forward</i>
        </router-link>
    </section>
</template>

<script>
import {
    ANALYTICS_DASHBOARD_ROUTES,
    getAnalyticsGoalById,
} from "../../utils/analytics-dashboard-goals"

const STATUS_LABELS = Object.freeze({
    live: "Live",
    live_with_limitations: "Live with limitations",
    deferred: "Deferred",
})

export default {
    name: "AnalyticsGoalPanel",
    props: {
        isDark: {
            type: Boolean,
            default: false,
        },
        activeGoalId: {
            type: String,
            default: "sales-revenue",
        },
    },
    computed: {
        activeGoal() {
            return getAnalyticsGoalById(this.activeGoalId) || getAnalyticsGoalById("sales-revenue")
        },
        programRoute() {
            return ANALYTICS_DASHBOARD_ROUTES.PROGRAM
        },
        statusIcon() {
            if (this.activeGoal.status === "live") return "verified"
            if (this.activeGoal.status === "deferred") return "pause_circle"
            return "info"
        },
        confidenceSummary() {
            if (this.activeGoal.status === "live") {
                return "The released operational scope is live. Remaining work is documented as governance or future enhancement."
            }
            if (this.activeGoal.status === "deferred") {
                return "This dashboard is intentionally outside the active analytics program."
            }
            const count = this.activeGoal.remainingWork?.length || 0
            return `${count} defined follow-up${count === 1 ? "" : "s"} remain; current metrics and limitations are visible on this page.`
        },
    },
    methods: {
        formatStatus(status) {
            return STATUS_LABELS[status] || String(status || "").replace(/_/g, " ")
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.analytics-confidence-strip {
    align-items: center;
    background: rgba($primaryColor, 0.025);
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    display: grid;
    gap: 14px;
    grid-template-columns: auto minmax(0, 1fr) auto;
    margin-top: 24px;
    padding: 14px 16px;

    &.dark {
        background: rgba($secondaryColorDark, 0.035);
        border-color: $darkBorderColor;
        color: $darkTextColor;
    }
}

.confidence-marker {
    align-items: center;
    background: rgba($successColor, 0.12);
    border-radius: 8px;
    color: darken($successColor, 14%);
    display: flex;
    height: 38px;
    justify-content: center;
    width: 38px;

    &.live_with_limitations {
        background: rgba($warningColor, 0.12);
        color: darken($warningColor, 22%);
    }

    &.deferred {
        background: rgba($textColor, 0.08);
        color: rgba($textColor, 0.62);
    }

    i { font-size: 20px; }
}

.dark .confidence-marker {
    background: rgba($successColorDark, 0.14);
    color: $successColorDark;

    &.live_with_limitations {
        background: rgba($warningColorDark, 0.14);
        color: $warningColorDark;
    }

    &.deferred {
        background: rgba($darkTextColor, 0.1);
        color: rgba($darkTextColor, 0.66);
    }
}

.confidence-meta {
    align-items: center;
    display: flex;
    gap: 8px;
    margin-bottom: 3px;
}

.confidence-copy {
    min-width: 0;

    h2 {
        color: $primaryColor;
        font-size: 14px;
        margin: 0;
    }

    > p {
        color: rgba($textColor, 0.66);
        font-size: 12px;
        line-height: 1.45;
        margin: 3px 0 0;
    }
}

.dark .confidence-copy {
    h2 { color: $secondaryColorDark; }
    > p { color: rgba($darkTextColor, 0.7); }
}

.eyebrow {
    color: rgba($textColor, 0.52);
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.04em;
    margin: 0;
    text-transform: uppercase;
}

.dark .eyebrow { color: rgba($darkTextColor, 0.58); }

.status-pill {
    border-radius: 999px;
    font-size: 9px;
    font-weight: 800;
    line-height: 1;
    padding: 4px 7px;
    text-transform: uppercase;
    white-space: nowrap;

    &.live {
        background: rgba($successColor, 0.13);
        color: darken($successColor, 16%);
    }

    &.live_with_limitations {
        background: rgba($warningColor, 0.13);
        color: darken($warningColor, 24%);
    }

    &.deferred {
        background: rgba($textColor, 0.08);
        color: rgba($textColor, 0.62);
    }
}

.dark .status-pill {
    &.live {
        background: rgba($successColorDark, 0.15);
        color: $successColorDark;
    }

    &.live_with_limitations {
        background: rgba($warningColorDark, 0.15);
        color: $warningColorDark;
    }

    &.deferred {
        background: rgba($darkTextColor, 0.1);
        color: rgba($darkTextColor, 0.66);
    }
}

.program-link {
    align-items: center;
    color: $primaryColor;
    display: inline-flex;
    font-size: 12px;
    font-weight: 700;
    gap: 5px;
    text-decoration: none;
    white-space: nowrap;

    i { font-size: 17px; }

    &:hover { text-decoration: underline; }
    &:active { transform: translateY(1px); }
}

.dark .program-link { color: $secondaryColorDark; }

@media (max-width: 720px) {
    .analytics-confidence-strip {
        align-items: start;
        grid-template-columns: auto minmax(0, 1fr);
    }

    .program-link {
        grid-column: 2;
        justify-self: start;
    }
}
</style>
